import React from "react";
import * as L from "leaflet";
import {
  Map as LeafletMap,
  TileLayer,
  LayersControl,
  WMSTileLayer,
  ScaleControl,
  Popup,
  CircleMarker,
} from "react-leaflet";
import PoiAreaDrawing from "./PoiAreaDrawing/PoiAreaDrawing";
import StaticDriverGeotiff from "./StaticDriverGeotiff/StaticDriverGeotiff";
import CustomPrintArea from "./CustomPrintArea/CustomPrintArea";
import SavingStatusIndicator from "../../../Components/Map/MapPanel/SavingStatusIndicator";
import LeafletRuler from "../../../Components/Map/WindTurbine/LeafletRuler";
import AnimationRecord from "./AnimationRecord/AnimationRecord";
import ExtraTopography from "../../../Components/Map/Topography/ExtraTopography";
import { LayerOptions } from "../../../common/TileLayers";
import "./StaticDriver.scss";
import {
  getPoiCursorCoords,
  addPoi,
  togglePoiCursorDisplay,
  addAoiPointWg,
  drawAoi,
  setResultsGeotiffVisibility,
  setResultsMapRef,
} from "../../../redux/actions/resultsActions";
import "leaflet-geotiff-2";
import "leaflet.browser.print/dist/leaflet.browser.print";
import html2canvas from "html2canvas";
import WMSCapabilities from "wms-capabilities";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { setWmsLayerNames } from "../../../redux/actions/mapActions";

const { BaseLayer } = LayersControl;

const InfoData = (props) => {
  const initMarker = (ref) => {
    if (ref) {
      ref.leafletElement.openPopup();
    }
  };

  return <CircleMarker ref={initMarker} {...props} />;
};

class StaticDriver extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      refs: [],
      currentPos: null,
      value: null,
      layerName: null,
    };

    this.valuePopup = React.createRef();
    this.arrayOfGeotiffRefs = React.createRef([]);
    this.staticDriverMapRef = React.createRef();
    this.canvasRef = React.createRef();

    this.handleRightClick = this.handleRightClick.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.onMapClick = this.onMapClick.bind(this);
    this.pointToLayer = this.pointToLayer.bind(this);
    this.loadWmsCapabilities = this.loadWmsCapabilities.bind(this);
  }

  loadWmsCapabilities() {
    const rawUrl = `${this.props.currentWmsMap?.url || ""}`.trim();
    if (!rawUrl) {
      return;
    }

    const urlCandidates = rawUrl.split(/\s+/).filter(Boolean);
    const normalizedUrl =
      urlCandidates.find((u) =>
        u.toLowerCase().includes("request=getcapabilities")
      ) || urlCandidates[0];
    const lower = normalizedUrl.toLowerCase();
    const hasCapabilitiesRequest =
      lower.includes("request=getcapabilities") &&
      lower.includes("service=wms");
    const separator = normalizedUrl.includes("?") ? "&" : "?";
    const capabilitiesUrl = hasCapabilitiesRequest
      ? normalizedUrl
      : `${normalizedUrl}${separator}service=WMS&request=GetCapabilities`;

    fetch(capabilitiesUrl)
      .then((response) => response.text())
      .then((text) => {
        const parsed = new WMSCapabilities().parse(text);
        const multiLayer = parsed?.Capability?.Layer?.Layer;
        if (!Array.isArray(multiLayer)) {
          console.warn("Invalid WMS capabilities response.", {
            url: capabilitiesUrl,
            parsedResult: parsed,
          });
          return;
        }

        const wmsLayersNames = [];
        if (multiLayer.length === 1 && multiLayer[0].Layer) {
          multiLayer[0].Layer.forEach((x) => {
            wmsLayersNames.push({ name: x.Name, title: x.Title });
          });
        } else {
          multiLayer.forEach((x) => {
            wmsLayersNames.push({ name: x.Name, title: x.Title });
          });
        }
        this.props.setWmsLayerNames(wmsLayersNames);
      });
  }

  componentDidMount() {
    const map = this.staticDriverMapRef.current.leafletElement;

    this._printMapControl = L.control
      .browserPrint({
        position: "topleft",
        title: "PrintMap",
        printModes: [
          L.BrowserPrint.Mode.Landscape("A4"),
          L.BrowserPrint.Mode.Portrait("A4"),
          L.BrowserPrint.Mode.Auto("A4"),
        ],
      })
      .addTo(map);

    L.BrowserPrint.Utils.registerLayer(
      L.LeafletGeotiff,
      "L.LeafletGeotiff",
      function (layer, utils) {
        // We need to clone options to properly handle multiple renderers.
        return new L.leafletGeotiff(layer._url, layer.options);
      }
    );

    // Add legend to print output - match the map's 2-column layout
    map.on(
      "browser-print-start",
      function (e) {
        // Get all legend elements from the map
        const legends = document.querySelectorAll(".res-geo-legend");
        if (legends && legends.length > 0) {
          // Determine if we need 2 columns (more than 4 legends)
          const needsTwoColumns = legends.length > 4;

          // Create a custom control to add the legends to the print map
          const PrintLegendControl = L.Control.extend({
            onAdd: function () {
              const container = L.DomUtil.create("div", "print-legend-container");
              // Inner grid wrapper so Leaflet/parent flex can't override our grid
              const gridInner = L.DomUtil.create("div", "print-legend-grid");
              if (needsTwoColumns) {
                const rowCount = Math.ceil(legends.length / 2);
                gridInner.style.setProperty("display", "grid", "important");
                gridInner.style.setProperty("grid-template-columns", "1fr 1fr", "important");
                gridInner.style.setProperty("grid-template-rows", `repeat(${rowCount}, max-content)`, "important");
                gridInner.style.setProperty("column-gap", "8px", "important");
                gridInner.style.setProperty("row-gap", "8px", "important");
                gridInner.style.setProperty("align-items", "start", "important");
              }

              legends.forEach(function (legend, index) {
                const wrapper = L.DomUtil.create("div", "legend-wrapper");
                wrapper.style.breakInside = "avoid";
                wrapper.style.pageBreakInside = "avoid";
                if (needsTwoColumns) {
                  const col = Math.floor(index / 3) + 1;
                  const row = (index % 3) + 1;
                  wrapper.style.setProperty("grid-column", String(col), "important");
                  wrapper.style.setProperty("grid-row", String(row), "important");
                  wrapper.style.setProperty("align-self", "start", "important");
                  wrapper.style.setProperty("justify-self", "start", "important");
                }

                const clonedLegend = legend.cloneNode(true);
                clonedLegend.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
                clonedLegend.style.borderRadius = "5px";
                clonedLegend.style.boxShadow = "0 0 10px rgba(0,0,0,0.2)";
                clonedLegend.style.fontSize = "12px";
                clonedLegend.style.lineHeight = "16px";
                clonedLegend.style.width = "120px";

                wrapper.appendChild(clonedLegend);
                gridInner.appendChild(wrapper);
              });

              container.appendChild(gridInner);
              return container;
            },
          });
          new PrintLegendControl({ position: "topright" }).addTo(e.printMap);
        }
      }
    );
    const that = this;

    const saveAsImage = () => {
      var node = document.getElementById("-print");
      html2canvas(node, { allowTaint: true, useCORS: true }).then(function (
        canvas
      ) {
        canvas.toBlob(function (blob) {
          let blobUrl = URL.createObjectURL(blob);
          let link = document.createElement("a");
          link.href = blobUrl;
          link.download = "aDefaultFileName.png";
          link.click();
        });
      });


    };

    map.on(
      "browser-print-end",
      function (e) {
        const pages = that.props.resultsJsonOrigin.pages[
          that.props.currentViewIndex
        ];
        const checkedIndices = [];
        pages.lyrs.forEach((lyr, index) => {
          if (lyr.checked) {
            checkedIndices.push(index);
          }
        });

        // 1. Hide geotiffs (toggle visibility off for each visible layer)
        checkedIndices.forEach((index) => {
          that.props.setResultsGeotiffVisibility({
            resultsGeotiffName: pages.lyrs[index].name,
            currentLayerIndex: index,
          });
        });

        // 2. Immediately show again the same layers (toggle back on next tick)
        setTimeout(() => {
          checkedIndices.forEach((index) => {
            that.props.setResultsGeotiffVisibility({
              resultsGeotiffName: pages.lyrs[index].name,
              currentLayerIndex: index,
            });
          });
        }, 0);
      }
    );

    this._saveImagePrintControl = L.control
      .browserPrint({
        position: "topleft",
        documentTitle: "SaveImage",
        title: "SaveImage",
        printModes: [
          L.BrowserPrint.Mode.Landscape("A4"),
          L.BrowserPrint.Mode.Portrait("A4"),
          L.BrowserPrint.Mode.Auto("A4"),
        ],
        printFunction: saveAsImage,
      })
      .addTo(map);

    const wireBrowserPrintLinks = () => {
      const printContainer =
        this._printMapControl.getContainer?.() ??
        this._printMapControl._container;
      const saveContainer =
        this._saveImagePrintControl.getContainer?.() ??
        this._saveImagePrintControl._container;
      const printAnchor =
        printContainer?.querySelector("a.leaflet-browser-print") ??
        printContainer?.querySelector("a");
      const saveImageAnchor =
        saveContainer?.querySelector("a.leaflet-browser-print") ??
        saveContainer?.querySelector("a");
      map.__cityClimateBrowserPrintLinks = {
        print: printAnchor,
        saveImage: saveImageAnchor,
      };
    };

    const finishBrowserPrintUi = () => {
      map.__cityClimateBrowserPrintHoverCleanups?.forEach((fn) => fn());
      const cleanups = [];
      map.__cityClimateBrowserPrintHoverCleanups = cleanups;

      wireBrowserPrintLinks();

      const attachToolbarHoverBridge = (control, otherControl) => {
        const container = control.getContainer?.() ?? control._container;
        const holder = container?.querySelector("ul.browser-print-holder");
        if (!container || !holder) {
          return { show: () => {}, leave: () => {} };
        }
        let hideTimer = null;
        const clearTimer = () => {
          if (hideTimer != null) {
            clearTimeout(hideTimer);
            hideTimer = null;
          }
        };
        const hide = () => {
          control._hidePageSizeButtons.call(control);
        };
        const scheduleHide = () => {
          clearTimer();
          hideTimer = setTimeout(hide, 220);
        };
        const onHolderEnter = () => clearTimer();
        const onHolderLeave = () => scheduleHide();
        L.DomEvent.on(holder, "mouseenter", onHolderEnter);
        L.DomEvent.on(holder, "mouseleave", onHolderLeave);
        cleanups.push(() => {
          L.DomEvent.off(holder, "mouseenter", onHolderEnter);
          L.DomEvent.off(holder, "mouseleave", onHolderLeave);
          clearTimer();
          hide();
        });
        return {
          show: () => {
            clearTimer();
            otherControl._hidePageSizeButtons.call(otherControl);
            control._displayPageSizeButtons.call(control);
          },
          leave: scheduleHide,
        };
      };

      const printHover = attachToolbarHoverBridge(
        this._printMapControl,
        this._saveImagePrintControl
      );
      const saveHover = attachToolbarHoverBridge(
        this._saveImagePrintControl,
        this._printMapControl
      );

      map.__cityClimateBrowserPrintHover = {
        enterPrint: printHover.show,
        leavePrint: printHover.leave,
        enterSave: saveHover.show,
        leaveSave: saveHover.leave,
      };
    };

    finishBrowserPrintUi();
    requestAnimationFrame(finishBrowserPrintUi);

    this.loadWmsCapabilities();
    this.props.setResultsMapRef(this.staticDriverMapRef.current);
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentWmsMap?.url !== prevProps.currentWmsMap?.url) {
      this.loadWmsCapabilities();
    }

    if (
      (this.state.currentPos &&
        this.props.currentViewIndex !== prevProps.currentViewIndex) ||
      (this.props.resultsJsonOrigin &&
        this.props.resultsJsonOrigin !== prevProps.resultsJsonOrigin)
    ) {
      this.setState({ currentPos: null });
    }

    if (
      prevProps.resultsJsonOrigin.pages[this.props.currentViewIndex].lyrs
        .length <
      this.props.resultsJsonOrigin.pages[this.props.currentViewIndex].lyrs
        .length
    ) {
      // this.props.setResultsGeotiffVisibility({
      //   resultsGeotiffName:
      //     this.props.resultsJsonOrigin.pages[this.props.currentViewIndex]
      //       .lyrs[0].name,
      //   currentLayerIndex: 0,
      //});
    }
  }

  handleRightClick() {
    if (this.props.inPoi) {
      this.props.togglePoiCursorDisplay();
    } else if (this.props.inAoi) {
      this.props.drawAoi();
    }
  }

  onMapClick = (e) => {
    if (this.props.inPoi && this.props.displayPoiOnMouseMove) {
      this.props.addPoi({
        lat: e.latlng.lat.toFixed(6),
        lng: e.latlng.lng.toFixed(6),
      });
    } else if (this.props.inAoi) {
      this.props.addAoiPointWg({
        lng: Number(e.latlng.lng.toFixed(6)),
        lat: Number(e.latlng.lat.toFixed(6)),
      });
    } else {
      const latLng = e.latlng;
      const frontCheckedGeotiff = this.props.resultsJsonOrigin.pages[
        this.props.currentViewIndex
      ].lyrs.find((x) => {
        return x.checked;
      });
      if (latLng && frontCheckedGeotiff) {
        const frontCheckedGeotiffName = frontCheckedGeotiff.name;
        const frontCheckedGeotiffId =
          this.props.resultsJsonOrigin.pages[this.props.currentViewIndex].name +
          frontCheckedGeotiff.name;

        const frontCheckedGeotiffRef = this.state.refs.find(
          (x) => x.props.id === frontCheckedGeotiffId
        );

        if (frontCheckedGeotiffRef) {
          const selectedPointValue =
            frontCheckedGeotiffRef.leafletElement.getValueAtLatLng(
              e.latlng.lat,
              e.latlng.lng
            );
          const unit = frontCheckedGeotiffRef?.props?.unit;
          let digPrec = 1;
          if (unit === "[m/s]") {
            digPrec = 2;
          }

          if (selectedPointValue) {
            const value =
              ` :` +
              "\xa0\xa0" +
              `${selectedPointValue.toFixed(digPrec)} ${unit}`;

            if (!isNaN(selectedPointValue)) {
              this.setState({
                value: value,
                currentPos: latLng,
                layerName: frontCheckedGeotiffName,
              });
            }
          }
        }
      }
    }
  };

  handleMouseMove = (e) => {
    this.props.getPoiCursorCoords([
      e.latlng.lat.toFixed(6),
      e.latlng.lng.toFixed(6),
    ]);
  };

  pointToLayer(feature, latlng) {
    //return L.marker(latlng, { icon: treeMarker });
  }

  render() {
    const { t } = this.props;
    return (
      <>
        <LeafletMap
          ref={this.staticDriverMapRef}
          center={this.props.center}
          zoom={this.props.zoom}
          maxZoom={19}
          className={`leaflet-map static-driver-results-map${
            this.props.inResultsMapExportMode
              ? " static-driver-print-controls-visible"
              : ""
          }`}
          onClick={this.onMapClick}
          onMouseMove={
            (this.props.inPoi && this.props.displayPoiOnMouseMove) ||
              this.props.inAoi
              ? this.handleMouseMove
              : null
          }
          onContextMenu={this.handleRightClick}
          preferCanvas
        >
          {this.state.currentPos && (
            <InfoData center={this.state.currentPos} radius={0} opacity={0}>
              <Popup>
                <div className="popup-container">
                  {t(this.state.layerName) + this.state.value}
                </div>
              </Popup>
            </InfoData>
          )}
          <ScaleControl position="bottomleft" imperial={false} />
          <LayersControl position="bottomleft">
            {LayerOptions.map((x) => {
              return (
                <BaseLayer key={x.name} name={t(x.name)} checked={x.checked}>
                  <TileLayer
                    attribution={x.attribution}
                    url={x.url}
                    crossOrigin="anonymous"
                  />
                </BaseLayer>
              );
            })}
            {this.props.wmsLayersNames.length > 0 &&
              this.props.wmsLayersNames.map((x) => (
                <BaseLayer key={x.name} name={x.title} checked={false}>
                  <WMSTileLayer
                    url={this.props.currentWmsMap.url}
                    layers={x.name}
                  />
                </BaseLayer>
              ))}
          </LayersControl>
          <PoiAreaDrawing />
          <StaticDriverGeotiff
            staticDriverMapRef={this.staticDriverMapRef}
            onResultsReady={(ref) => {
              if (
                ref !== null &&
                ref !== undefined &&
                !this.state.refs.some((x) => x.props.id === ref.props.id)
              ) {
                this.setState({
                  refs: [...this.state.refs, ref],
                });
              }
            }}
          />
          <ExtraTopography />
          {this.props.inExportSettings && <CustomPrintArea />}

          <LeafletRuler style={{ top: "194px" }} />
          <SavingStatusIndicator
            style={{
              top: "214px",
              position: "fixed",
              zIndex: "inherit",
              left: "194px",
            }}
          />
        </LeafletMap>
        {this.props.inRecordMode && (
          <AnimationRecord
            isOpen={this.props.inRecordMode}
            canvasRef={this.canvasRef}
            staticDriverMapRef={this.staticDriverMapRef}
          />
        )}
        <canvas ref={this.canvasRef} style={{ display: "none" }} />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    inPoi: state.results.inPoi,
    inAoi: state.results.inAoi,
    displayPoiOnMouseMove: state.results.displayPoiOnMouseMove,
    currentViewIndex: state.results.currentViewIndex,
    resultsJsonOrigin: state.results.resultsJsonOrigin,
    inExportSettings: state.results.inExportSettings,
    inResultsMapExportMode: state.results.inResultsMapExportMode,
    currentLayerIndex: state.results.currentLayerIndex,
    inRecordMode: state.results.inRecordMode,
    currentWmsMap: state.userSettings?.settings?.wsMaps?.currentValue,
    wmsLayersNames: state.map.wmsLayersNames,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getPoiCursorCoords: (payload) => dispatch(getPoiCursorCoords(payload)),
    addPoi: (payload) => dispatch(addPoi(payload)),
    addAoiPointWg: (payload) => dispatch(addAoiPointWg(payload)),
    togglePoiCursorDisplay: (payload) =>
      dispatch(togglePoiCursorDisplay(payload)),
    drawAoi: (payload) => dispatch(drawAoi(payload)),
    setResultsGeotiffVisibility: (payload) =>
      dispatch(setResultsGeotiffVisibility(payload)),
    setResultsMapRef: (payload) => dispatch(setResultsMapRef(payload)),
    setWmsLayerNames: (payload) => dispatch(setWmsLayerNames(payload)),
  };
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(StaticDriver)
);
